import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';
import type { EmployeeStatusDto } from '../models/types';

// Fluent Badge color name -> shadcn Badge class, preserving status color SEMANTICS.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};
function badgeClass(c: string): string {
  return BADGE_CLASS[c] || BADGE_CLASS.informative;
}

interface EmployeeStatusCardProps {
  employees: EmployeeStatusDto[];
}

export const EmployeeStatusCard: React.FC<EmployeeStatusCardProps> = ({ employees = [] }) => {
  // API may return status as int enum or string
  const statusToString = (s: any): string => {
    if (typeof s === 'string') return s;
    const map: Record<number, string> = { 0: 'Inactive', 1: 'Active', 2: 'Idle', 3: 'Busy' };
    return map[s] ?? `Status ${s}`;
  };

  const getStatusBadge = (status: any) => {
    const str = statusToString(status);
    // Preserve Fluent color semantics: active=success, idle=warning, busy=brand(important), else subtle.
    const color = str.toLowerCase() === 'active' ? 'success'
      : str.toLowerCase() === 'idle' ? 'warning'
      : str.toLowerCase() === 'busy' ? 'brand'
      : 'subtle';
    return <Badge variant="outline" className={badgeClass(color)}>{str}</Badge>;
  };

  return (
    <Card>
      <div className="flex items-center gap-3 p-4 pb-0">
        <Users className="h-6 w-6 text-accent-text" />
        <div>
          <p className="text-sm font-semibold text-foreground">AI Employees</p>
          <span className="text-xs text-muted-foreground">{employees.length} employees</span>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {employees.map((employee) => (
          <div
            key={employee.employeeId}
            className="border-b border-border"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            <div>
              <p className="text-sm">{employee.employeeName}</p>
              <span className="text-xs text-muted-foreground">
                {employee.employeeLevel} • {employee.currentConversationCount} active
              </span>
            </div>
            {getStatusBadge(employee.status)}
          </div>
        ))}
      </div>
    </Card>
  );
};
